/**
 * Automatic screen scaling.
 *
 * Laptops running Windows display scaling (125% / 150%) give the browser far fewer
 * CSS pixels than the editor was designed for. We compensate by zooming the app root
 * down so it lays out as if the window were REFERENCE_WIDTH wide.
 * Only ever scales down, and never below MIN_SCALE so text stays readable.
 *
 * The zoom sits on #root rather than <html> on purpose: MUI, Popper and Radix popups
 * portal into <body>, outside #root. They position themselves from the trigger's
 * on-screen rect, which is only correct in an unzoomed coordinate space. The trade-off
 * is that popups and dialogs render at 100% while the app behind them is scaled.
 */

export const REFERENCE_WIDTH = 1920;
export const MIN_SCALE = 0.8;
/** Below this layout width the root stops shrinking and scrolls sideways instead. */
export const MIN_LAYOUT_WIDTH = 1200;

export const SCREEN_SCALING_STORAGE_KEY = "screenScaling";
export const SCREEN_SCALING_QUERY_PARAM = "screenScaling";
export const APP_ZOOM_CHANGE_EVENT = "appzoomchange";
export const APP_ZOOM_CSS_VAR = "--app-zoom";
export const NARROW_VIEWPORT_CLASS = "pi-narrow-viewport";

export const computeScreenScale = (viewportWidth: number): number => {
  if (!Number.isFinite(viewportWidth) || viewportWidth <= 0) return 1;
  const scale = Math.min(1, Math.max(MIN_SCALE, viewportWidth / REFERENCE_WIDTH));
  // Round so sub-pixel resize jitter doesn't trigger a relayout.
  return Math.round(scale * 1000) / 1000;
};

/**
 * Kill switch: `?screenScaling=off` disables scaling and persists that choice,
 * `?screenScaling=on` clears it again. Also honoured straight from localStorage.
 */
export const isScreenScalingDisabled = (): boolean => {
  try {
    const param = new URLSearchParams(window.location.search).get(SCREEN_SCALING_QUERY_PARAM);
    if (param === "off") localStorage.setItem(SCREEN_SCALING_STORAGE_KEY, "off");
    if (param === "on") localStorage.removeItem(SCREEN_SCALING_STORAGE_KEY);
    return localStorage.getItem(SCREEN_SCALING_STORAGE_KEY) === "off";
  } catch {
    return false;
  }
};

let currentZoom = 1;

/** The zoom currently applied to the document (1 when scaling is off). */
export const getAppZoom = (): number => currentZoom;

/**
 * `getBoundingClientRect()` and pointer coordinates are in on-screen pixels, but CSS
 * lengths inside the zoomed document get scaled again. Convert before using a measured
 * value as a `px` style. Ratios (e.g. `(clientX - rect.left) / rect.width`) need no conversion.
 */
export const toLayoutPx = (screenPx: number): number => screenPx / currentZoom;

/**
 * The zoom an element is actually rendered at: on-screen width over layout width.
 * Measured from the DOM rather than taken from the module state, so it is right in
 * any browser zoom implementation and for counter-zoomed regions. Rounded to two
 * decimals so sub-pixel noise does not change it; falls back to the app zoom for
 * an element without layout.
 */
export const getElementZoom = (element: HTMLElement): number => {
  const layoutWidth = element.offsetWidth;
  if (layoutWidth <= 0) return currentZoom;
  return Math.round((element.getBoundingClientRect().width / layoutWidth) * 100) / 100;
};

/**
 * The zoom for menu content that portals into <body> (Radix DropdownMenu): the
 * trigger's rendered zoom, so the menu matches the app instead of showing at 1x.
 * Only the content is zoomed; the positioning wrapper stays unzoomed, so Radix
 * still places it exactly. 1 without a trigger.
 */
export const getPortalZoom = (trigger: HTMLElement | null): number =>
  trigger ? getElementZoom(trigger) : 1;

/**
 * Computes the scale for the current window and applies it to #root.
 * `window.innerWidth` is not affected by CSS zoom, so this never feeds back on itself.
 * `--app-zoom` is set on #root too, so viewport-unit styles in portals (which aren't
 * zoomed) fall back to 1.
 */
export const applyScreenScale = (): number => {
  const viewportWidth = window.innerWidth;
  const zoom = isScreenScalingDisabled() ? 1 : computeScreenScale(viewportWidth);
  const appRoot = document.getElementById("root");

  if (appRoot) {
    if (zoom === 1) {
      appRoot.style.removeProperty("zoom");
    } else {
      appRoot.style.setProperty("zoom", String(zoom));
    }
    appRoot.style.setProperty(APP_ZOOM_CSS_VAR, String(zoom));
  }
  document.documentElement.classList.toggle(
    NARROW_VIEWPORT_CLASS,
    viewportWidth / zoom < MIN_LAYOUT_WIDTH
  );

  if (zoom !== currentZoom) {
    currentZoom = zoom;
    window.dispatchEvent(new Event(APP_ZOOM_CHANGE_EVENT));
  }
  return zoom;
};
